<template>
    <div class="pa-4">
        <v-card class="mt-2 py-2">
            <v-toolbar flat>
                <v-toolbar-title>Outlet Detail</v-toolbar-title>
                <v-divider class="mx-4" inset vertical />
                <v-spacer />
                <v-btn color="grey" dark class="mb-2 mr-2" @click="backToOutlet()" small>
                    <v-icon small>mdi-arrow-left</v-icon>
                    Back
                </v-btn>
            </v-toolbar>
            <div class="mt-3" v-if="isLoading">
                <v-row class="mx-5 py-0 compact-form" align="center">
                    <v-col cols="6">
                        <v-skeleton-loader type="text" height="50" />
                    </v-col>
                    <v-col cols="6">
                        <v-skeleton-loader type="text" height="50" />
                    </v-col>
                    <v-col cols="6">
                        <v-skeleton-loader type="text" height="50" />
                    </v-col>
                    <v-col cols="6">
                        <v-skeleton-loader type="text" height="50" />
                    </v-col>
                </v-row>
            </div>
            <div class="mt-3" v-else>
                <v-row class="mx-5 py-0 compact-form" align="center">
                    <v-col cols="6">
                        <span class="pb-4 font-weight-bold">Outlet Code</span>
                        <v-text-field v-model="outlet.code" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                    <v-col cols="6">
                        <span class="font-weight-bold">Opening</span>
                        <v-text-field v-model="outlet.opening" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                </v-row>
                <v-row class="mx-5 py-0 compact-form" align="center">
                    <v-col cols="6">
                        <span class="font-weight-bold">Outlet Name</span>
                        <v-text-field v-model="outlet.name" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                    <v-col cols="6">
                        <span class="font-weight-bold">Remaining</span>
                        <v-text-field v-model="outlet.remaining" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                </v-row>
                <v-row class="mx-5 py-0 compact-form" align="center">
                    <v-col cols="6">
                        <span class="font-weight-bold">Category</span>
                        <v-text-field :value="getCategoryName(outlet.outlet_category_code)" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                    <v-col cols="6">
                        <span class="font-weight-bold">Call Center</span>
                        <v-text-field :value="getCallCenterName(outlet.call_center_code)" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                </v-row>
                <v-row class="mx-5 py-0 compact-form">
                    <v-col cols="6">
                        <div>
                            <span class="font-weight-bold">Address</span>
                            <v-textarea outlined v-model="outlet.address" dense rows="2" readonly />
                        </div>
                        <div>
                            <span class="font-weight-bold">Email</span>
                            <v-text-field v-model="outlet.email" outlined rounded dense hide-details="auto" readonly />
                        </div>
                        <div>
                            <span class="font-weight-bold">Notes</span>
                            <v-textarea outlined v-model="outlet.notes" dense rows="2" readonly />
                        </div>
                    </v-col>
                    <v-col cols="6">
                        <div>
                            <span class="font-weight-bold">Sales Schedule</span>
                            <v-chip-group>
                                <v-chip v-for="schedule in outlet.delivery_schedules" :key="schedule" color="primary" outlined small>
                                    {{ getDeliveryScheduleName(schedule) }}
                                </v-chip>
                            </v-chip-group>
                        </div>
                        <div class="mt-2">
                            <span class="font-weight-bold">Delivery</span>
                            <v-simple-table dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th v-for="(delivery, idx) in outlet.delivery_schedules" :key="idx" class="text-center">
                                                {{ getDeliveryScheduleName(delivery) }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td v-for="(delivery, idx) in outlet.delivery_schedules2" :key="idx" class="text-center">
                                                {{ delivery }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </div>
                        <div class="mt-2">
                            <span class="font-weight-bold">Arrival</span>
                            <v-simple-table dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th v-for="(delivery, idx) in outlet.delivery_schedules" :key="idx" class="text-center">
                                                {{ getDeliveryScheduleName(delivery) }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td v-for="(arrival, idx) in outlet.arrive_schedules" :key="idx" class="text-center">
                                                {{ arrival }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </div>
                    </v-col>
                </v-row>
                <v-row class="mx-5 py-0 compact-form">
                    <v-col cols="6">
                        <span class="font-weight-bold">Operational Team</span>
                        <v-text-field :value="getOperationalTeamName(outlet.operational_team_code)" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                    <v-col cols="6">
                        <v-chip :color="outlet.is_inner_city ? 'green' : 'orange'" text-color="white" class="mt-6">
                            {{ outlet.is_inner_city ? 'Dalam Kota' : 'Luar Kota' }}
                        </v-chip>
                    </v-col>
                </v-row>
                <v-row class="mx-5 py-0 compact-form">
                    <v-col cols="6">
                        <span class="font-weight-bold">Iseller Outlet Code</span>
                        <v-text-field v-model="outlet.iseller_outlet_code" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                    <v-col cols="6">
                        <span class="font-weight-bold">Iseller Outlet ID</span>
                        <v-text-field v-model="outlet.iseller_outlet_id" outlined rounded dense hide-details="auto" readonly />
                    </v-col>
                </v-row>
                <v-row class="mx-5 py-0 compact-form">
                    <v-col cols="12">
                        <v-card outlined>
                            <v-card-title class="text-h6 py-2">Coordinator</v-card-title>
                            <v-simple-table fixed-header dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th v-show="isDeliverySchedule(1)">Minggu</th>
                                            <th v-show="isDeliverySchedule(2)">Senin</th>
                                            <th v-show="isDeliverySchedule(3)">Selasa</th>
                                            <th v-show="isDeliverySchedule(4)">Rabu</th>
                                            <th v-show="isDeliverySchedule(5)">Kamis</th>
                                            <th v-show="isDeliverySchedule(6)">Jumat</th>
                                            <th v-show="isDeliverySchedule(7)">Sabtu</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td v-show="isDeliverySchedule(1)">{{ getCoordinatorName(outlet, 1) }}</td>
                                            <td v-show="isDeliverySchedule(2)">{{ getCoordinatorName(outlet, 2) }}</td>
                                            <td v-show="isDeliverySchedule(3)">{{ getCoordinatorName(outlet, 3) }}</td>
                                            <td v-show="isDeliverySchedule(4)">{{ getCoordinatorName(outlet, 4) }}</td>
                                            <td v-show="isDeliverySchedule(5)">{{ getCoordinatorName(outlet, 5) }}</td>
                                            <td v-show="isDeliverySchedule(6)">{{ getCoordinatorName(outlet, 6) }}</td>
                                            <td v-show="isDeliverySchedule(7)">{{ getCoordinatorName(outlet, 7) }}</td>
                                        </tr>
                                        <tr>
                                            <td v-show="isDeliverySchedule(1)" class="text-caption grey--text">{{ getLineName(outlet, 1) }}</td>
                                            <td v-show="isDeliverySchedule(2)" class="text-caption grey--text">{{ getLineName(outlet, 2) }}</td>
                                            <td v-show="isDeliverySchedule(3)" class="text-caption grey--text">{{ getLineName(outlet, 3) }}</td>
                                            <td v-show="isDeliverySchedule(4)" class="text-caption grey--text">{{ getLineName(outlet, 4) }}</td>
                                            <td v-show="isDeliverySchedule(5)" class="text-caption grey--text">{{ getLineName(outlet, 5) }}</td>
                                            <td v-show="isDeliverySchedule(6)" class="text-caption grey--text">{{ getLineName(outlet, 6) }}</td>
                                            <td v-show="isDeliverySchedule(7)" class="text-caption grey--text">{{ getLineName(outlet, 7) }}</td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row class="mx-5 py-0 compact-form">
                    <v-col>
                        <v-card outlined>
                            <v-card-title class="text-h6 py-2">Hourly Percentage</v-card-title>
                            <v-simple-table class="spreadsheet-table flex-table" fixed-header dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="header-cell">Hari</th>
                                            <th class="header-cell text-center">Min Stock</th>
                                            <th class="header-cell text-center">Max Stock</th>
                                            <th class="header-cell text-center">Min Stock 250</th>
                                            <th class="header-cell text-center">Max Stock 250</th>
                                            <th class="header-cell text-center" v-for="defaultTime in filteredDefaultTimes" :key="defaultTime.id">
                                                {{ defaultTime.text }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, idt) in outlet.weekdays_hours_percent" :key="idt">
                                            <td class="header-cell">{{ defaultDays[idt].value }}</td>
                                            <td class="text-center">{{ item.min_stock }}</td>
                                            <td class="text-center">{{ item.max_stock }}</td>
                                            <td class="text-center">{{ item.min_stock2 }}</td>
                                            <td class="text-center">{{ item.max_stock2 }}</td>
                                            <td class="text-center" v-for="(time, idx) in item.times" :key="idx" v-show="idx >= indexTimeStart && idx <= indexTimeEnd">
                                                {{ time }}%
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-card>
                    </v-col>
                </v-row>
                <v-card outlined class="mx-5 compact-form">
                    <v-card-title class="text-h6 py-2">Expired</v-card-title>
                    <v-simple-table fixed-header dense>
                        <template v-slot:default>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th class="text-center">Minggu</th>
                                    <th class="text-center">Senin</th>
                                    <th class="text-center">Selasa</th>
                                    <th class="text-center">Rabu</th>
                                    <th class="text-center">Kamis</th>
                                    <th class="text-center">Jumat</th>
                                    <th class="text-center">Sabtu</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="font-weight-bold">Exp</td>
                                    <td class="text-center">{{ outlet.expired_sun }}</td>
                                    <td class="text-center">{{ outlet.expired_mon }}</td>
                                    <td class="text-center">{{ outlet.expired_tue }}</td>
                                    <td class="text-center">{{ outlet.expired_wed }}</td>
                                    <td class="text-center">{{ outlet.expired_thu }}</td>
                                    <td class="text-center">{{ outlet.expired_fri }}</td>
                                    <td class="text-center">{{ outlet.expired_sat }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                </v-card>
                <v-card outlined class="mx-5 compact-form">
                    <v-card-title class="text-h6 py-2">Default PPIC</v-card-title>
                    <v-tabs v-model="selectedTab" background-color="transparent" color="primary">
                        <v-tab v-for="(botol, bidx) in sortedBotols" :key="bidx">
                            {{ getBotolDescription(botol) }}
                        </v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="selectedTab">
                        <v-tab-item v-for="(botol, bidx) in sortedBotols" :key="bidx">
                            <v-simple-table class="spreadsheet-table pt-5 flex-table" fixed-header dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="header-cell" colspan="2"></th>
                                            <th class="header-cell text-center">Minggu</th>
                                            <th class="header-cell text-center">Senin</th>
                                            <th class="header-cell text-center">Selasa</th>
                                            <th class="header-cell text-center">Rabu</th>
                                            <th class="header-cell text-center">Kamis</th>
                                            <th class="header-cell text-center">Jumat</th>
                                            <th class="header-cell text-center">Sabtu</th>
                                        </tr>
                                        <tr>
                                            <th class="header-cell text-center" colspan="2">Target Sales</th>
                                            <td class="text-center" v-for="(target, i) in getTargets(botol).days" :key="i">
                                                {{ target }}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th class="header-cell text-center">Kode</th>
                                            <th class="header-cell text-center">Produk</th>
                                            <th class="header-cell text-center">Minggu (%)</th>
                                            <th class="header-cell text-center">Senin (%)</th>
                                            <th class="header-cell text-center">Selasa (%)</th>
                                            <th class="header-cell text-center">Rabu (%)</th>
                                            <th class="header-cell text-center">Kamis (%)</th>
                                            <th class="header-cell text-center">Jumat (%)</th>
                                            <th class="header-cell text-center">Sabtu (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(product, idxp) in getProducts(botol)" :key="idxp">
                                            <td class="header-cell">{{ idxp + 1 }}. {{ product.code }}</td>
                                            <td class="header-cell">{{ product.name }}</td>
                                            <td class="text-center">{{ product.target_sun }}</td>
                                            <td class="text-center">{{ product.target_mon }}</td>
                                            <td class="text-center">{{ product.target_tue }}</td>
                                            <td class="text-center">{{ product.target_wed }}</td>
                                            <td class="text-center">{{ product.target_thu }}</td>
                                            <td class="text-center">{{ product.target_fri }}</td>
                                            <td class="text-center">{{ product.target_sat }}</td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th class="header-cell"></th>
                                            <th class="header-cell">Total</th>
                                            <th class="header-cell text-center">{{ getTotalSun(botol) }}</th>
                                            <th class="header-cell text-center">{{ getTotalMon(botol) }}</th>
                                            <th class="header-cell text-center">{{ getTotalTue(botol) }}</th>
                                            <th class="header-cell text-center">{{ getTotalWed(botol) }}</th>
                                            <th class="header-cell text-center">{{ getTotalThu(botol) }}</th>
                                            <th class="header-cell text-center">{{ getTotalFri(botol) }}</th>
                                            <th class="header-cell text-center">{{ getTotalSat(botol) }}</th>
                                        </tr>
                                    </tfoot>
                                </template>
                            </v-simple-table>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card>
            </div>
        </v-card>
    </div>
</template>

<script>
export default {
    data() {
        return {
            isLoading: true,
            selectedTab: 0,
            outlet: {
                code: '',
                name: '',
                outlet_category_code: '',
                address: '',
                email: '',
                notes: '',
                call_center_code: '',
                delivery_schedules: [],
                delivery_schedules2: [],
                arrive_schedules: [],
                operational_team_code: '',
                opening: '',
                remaining: '',
                coordinator_mon: 0,
                coordinator_tue: 0,
                coordinator_wed: 0,
                coordinator_thu: 0,
                coordinator_fri: 0,
                coordinator_sat: 0,
                coordinator_sun: 0,
                expired_mon: 0,
                expired_tue: 0,
                expired_wed: 0,
                expired_thu: 0,
                expired_fri: 0,
                expired_sat: 0,
                expired_sun: 0,
                sales_targets: [],
                weekdays_hours_percent: [],
                botols: [],
                line_mon: '',
                line_tue: '',
                line_wed: '',
                line_thu: '',
                line_fri: '',
                line_sat: '',
                line_sun: '',
                targets: [],
                iseller_outlet_code: '',
                iseller_outlet_id: '',
                is_inner_city: true,
            },
            categories: [],
            call_centers: [],
            users: [],
            coordinators: [],
            lines: [],
            botols: [],
            defaultTimes: [],
            indexTimeStart: 0,
            indexTimeEnd: 23,
            delivery_schedules: [
                { key: 1, value: 'Minggu' },
                { key: 2, value: 'Senin' },
                { key: 3, value: 'Selasa' },
                { key: 4, value: 'Rabu' },
                { key: 5, value: 'Kamis' },
                { key: 6, value: 'Jumat' },
                { key: 7, value: 'Sabtu' },
            ],
        }
    },
    computed: {
        isDeliverySchedule() {
            return (num) => {
                return this.outlet.delivery_schedules.includes(num)
            }
        },
        sortedBotols() {
            if (!this.outlet.botols || this.outlet.botols.length === 0) {
                return []
            }
            return [...this.outlet.botols].sort((a, b) => {
                return parseInt(a) - parseInt(b)
            })
        },
        filteredDefaultTimes() {
            return this.defaultTimes.filter((item, index) => {
                return index >= this.indexTimeStart && index <= this.indexTimeEnd 
            })
        },
        defaultDays() {
            return this.$store.state.outlet.default_days
        },
    },
    async created() {
        await this.loadMasterData()
        const outlet_code = this.$route.params.outlet_code
        if (outlet_code) {
            await this.loadOutlet(outlet_code)
        }
    },
    methods: {
        async loadMasterData() {
            try {
                try {
                    this.categories = (await this.$store.dispatch('outletcategory/getObjs')).data.data || []
                } catch (error) {
                    console.warn('Cannot load categories:', error)
                    this.categories = []
                }

                try {
                    this.call_centers = (await this.$store.dispatch('callcenter/getObjs')).data.data || []
                } catch (error) {
                    console.warn('Cannot load call centers:', error)
                    this.call_centers = []
                }

                try {
                    this.users = (await this.$store.dispatch('getObjs', {apidomain: 'users', parameter: ''})).data.data || []
                    this.users = this.users.filter(user => user.group == 'OP')
                } catch (error) {
                    console.warn('Cannot load users:', error)
                    this.users = []
                }

                try {
                    this.coordinators = (await this.$store.dispatch('coordinator/getObjs')).data.data || []
                } catch (error) {
                    console.warn('Cannot load coordinators:', error)
                    this.coordinators = []
                }

                try {
                    let dataMiscCodes = this.$store.state.misccode.objs
                    this.lines = dataMiscCodes.filter((i => i.type == 'Vehicle')) || []
                    this.botols = dataMiscCodes.filter((i => i.type.includes('Botol')))
                        .sort((a,b) => {
                            return parseInt(a.code) - parseInt(b.code)
                        })
                } catch (error) {
                    console.warn('Cannot load lines:', error)
                    this.lines = []
                    this.botols = []
                }

                this.generateDefaultTime()
            } catch (error) {
                console.error('Error loading master data:', error)
            }
        },
        generateDefaultTime() {
            this.defaultTimes = []
            for (let i = 0; i < 24; i++) {
                this.defaultTimes.push({
                    id: i,
                    text: i > 9 ? `${i}:00` : `0${i}:00`
                })
            }
        },
        async loadOutlet(code) {
            try {
                this.isLoading = true
                const data = await this.$store.dispatch('outlet/getObj', code)
                this.outlet = data

                if (this.outlet.outlet_category_code) {
                    const category = this.categories.find(c => c.code === this.outlet.outlet_category_code)
                    if (category) {
                        this.indexTimeStart = this.defaultTimes.findIndex(item => item.text == category.operational_hour_start)
                        this.indexTimeEnd = this.defaultTimes.findIndex(item => item.text == category.operational_hour_end)
                    }
                }
            } catch (error) {
                console.error('Error loading outlet detail:', error)
                this.$store.commit('setSnackbar', {saveError: true, errorMessage: 'Gagal memuat detail outlet'})
            } finally {
                this.isLoading = false
            }
        },
        getCategoryName(code) {
            if (!code) return '-'
            if (this.categories.length === 0) return code
            const category = this.categories.find(c => c.code === code)
            return category ? category.name : code
        },
        getCallCenterName(code) {
            if (!code) return '-'
            if (this.call_centers.length === 0) return code
            const callCenter = this.call_centers.find(c => c.code === code)
            return callCenter ? callCenter.name : code
        },
        getOperationalTeamName(code) {
            if (!code) return '-'
            if (this.users.length === 0) return code
            const user = this.users.find(u => u.username === code)
            return user ? user.fullname : code
        },
        getDeliveryScheduleName(key) {
            const schedule = this.delivery_schedules.find(s => s.key === key)
            return schedule ? schedule.value : key
        },
        getCoordinatorName(outlet, dayKey) {
            const coordinatorField = {
                1: 'coordinator_sun',
                2: 'coordinator_mon',
                3: 'coordinator_tue',
                4: 'coordinator_wed',
                5: 'coordinator_thu',
                6: 'coordinator_fri',
                7: 'coordinator_sat'
            }
            const fieldName = coordinatorField[dayKey]
            const code = outlet[fieldName]
            if (!code) return '-'
            if (this.coordinators.length === 0) return code
            const coordinator = this.coordinators.find(c => c.code === code)
            return coordinator ? coordinator.name : code
        },
        getLineName(outlet, dayKey) {
            const lineField = {
                1: 'line_sun',
                2: 'line_mon',
                3: 'line_tue',
                4: 'line_wed',
                5: 'line_thu',
                6: 'line_fri',
                7: 'line_sat'
            }
            const fieldName = lineField[dayKey]
            const code = outlet[fieldName]
            if (!code) return '-'
            if (this.lines.length === 0) return code
            const line = this.lines.find(l => l.code === code)
            return line ? `${line.description} : ${line.misc_field}` : code
        },
        getBotolDescription(code) {
            const botol = this.botols.find(b => b.code === code)
            return botol ? botol.description : code
        },
        getTargets(size) {
            if (!this.outlet.targets) return { size: '', days: [0,0,0,0,0,0,0] }
            const target = this.outlet.targets.find(t => t.size === size)
            return target || { size: '', days: [0,0,0,0,0,0,0] }
        },
        getProducts(size) {
            if (!this.outlet.sales_targets) return []
            return this.outlet.sales_targets.filter(p => p.size === size)
                .sort((a, b) => (a.sort_no || 0) - (b.sort_no || 0))
        },
        getTotalSun(size) {
            return this.getProducts(size).reduce((total, item) => total + Number(item.target_sun || 0), 0)
        },
        getTotalMon(size) {
            return this.getProducts(size).reduce((total, item) => total + Number(item.target_mon || 0), 0)
        },
        getTotalTue(size) {
            return this.getProducts(size).reduce((total, item) => total + Number(item.target_tue || 0), 0)
        },
        getTotalWed(size) {
            return this.getProducts(size).reduce((total, item) => total + Number(item.target_wed || 0), 0)
        },
        getTotalThu(size) {
            return this.getProducts(size).reduce((total, item) => total + Number(item.target_thu || 0), 0)
        },
        getTotalFri(size) {
            return this.getProducts(size).reduce((total, item) => total + Number(item.target_fri || 0), 0)
        },
        getTotalSat(size) {
            return this.getProducts(size).reduce((total, item) => total + Number(item.target_sat || 0), 0)
        },
        backToOutlet() {
            this.$router.push('/outlet')
        },
    },
}
</script>

<style>
.v-text-field fieldset, .v-text-field .v-input__control, .v-text-field .v-input__slot {
    border-radius: 7px!important;
}

.compact-form .col {
    padding: 5px!important;
}

.spreadsheet-table {
    border-collapse: collapse;
}

.spreadsheet-table .header-cell {
    background-color: #f5f5f5;
    font-weight: bold;
    border: 1px solid #ddd;
    padding: 8px;
}

.spreadsheet-table td {
    border: 1px solid #ddd;
    padding: 8px;
}

.flex-table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
}

.v-data-table table tfoot th {
    position: sticky;
    bottom: 0;
    background: #FFF;
    font-weight: bold;
}
</style>


